import { BudgetPhases, MANTY_ORDERED_BUDGET_PHASES } from 'business/constants';
import {
    INSCRIPTION_ORIGINAL_AMOUNT,
    INSCRIPTION_ORIGINAL_COMMENT,
} from 'routes/BudgetModule/config/constants';
import {
    getBudgetProposalFromInscription,
    getFieldValueFromPhase,
    InscriptionT,
} from 'routes/BudgetModule/shared/entities/inscription';
import { budgetCurrencyFormatter } from 'ui/AgGridReact/formatters';
import {
    BudgetProposalT,
    InscriptionWithMetadata,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { isInscriptionVoted } from 'routes/BudgetModule/helpers/statusHelpers';
import { isFirstActivePhase } from 'routes/BudgetModule/shared/phases/helpers';

const concatenateInscription = (
    amount: string | undefined,
    comment: string | undefined,
    t: TranslationT,
): string => {
    if (comment === 'unknown' || !comment) {
        // TODO: we could do this API side..
        return amount === 'unknown' || !amount
            ? t('budgetProposal.newInscription')
            : budgetCurrencyFormatter({ value: amount });
    }

    return `${budgetCurrencyFormatter({ value: amount }) || '-'} : ${comment}`;
};

export const getOriginalOrCurrentInscriptionValue = (
    data: InscriptionT,
    phase: BudgetPhases,
    t: TranslationT,
): string => {
    if (isFirstActivePhase(phase)) {
        return concatenateInscription(
            (data[INSCRIPTION_ORIGINAL_AMOUNT] ?? '').toString(),
            (data[INSCRIPTION_ORIGINAL_COMMENT] ?? '').toString(),
            t,
        )!;
    }

    const amount =
        getFieldValueFromPhase(
            MANTY_ORDERED_BUDGET_PHASES[0],
            'amount',
            data,
        ) ?? undefined;
    const comment =
        getFieldValueFromPhase(
            MANTY_ORDERED_BUDGET_PHASES[0],
            'comment',
            data,
        ) ?? undefined;

    return concatenateInscription(amount?.toString(), comment?.toString(), t);
};

export const inscriptionTextComparator = (
    a: { text: string } | undefined,
    b: { text: string } | undefined,
): number => {
    if (!a || !a.text) {
        return -1;
    }
    if (!b || !b.text) {
        return 1;
    }
    if (a.text < b.text) {
        return -1;
    }
    if (a.text > b.text) {
        return 1;
    }
    return 0;
};

export const areInscriptionsFromSamePhase = (
    inscriptions: InscriptionWithMetadata[],
    budgetProposals: BudgetProposalT[],
): boolean => {
    if (inscriptions.length === 0) {
        // no inscriptions means we cannot get the phase so we return false
        return false;
    }
    const uniquePhases = new Set(
        inscriptions.map(
            (inscription) =>
                getBudgetProposalFromInscription(
                    inscription.inscription,
                    budgetProposals,
                ).phase,
        ),
    );
    return uniquePhases.size === 1;
};

export const getInscriptionInformation = (
    data: InscriptionWithMetadata | undefined,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    inscriptionIdColumn?: keyof InscriptionT,
): {
    inscription: InscriptionT | null;
    inscriptionId: string;
    disabledByResource: boolean;
    isSupplementaryNotEditable: boolean;
    canEdit: boolean;
    phaseEditable: boolean;
    budgetProposal: BudgetProposalT | undefined;
} => {
    if (!data) {
        return {
            inscription: null,
            inscriptionId: '',
            canEdit: false,
            disabledByResource: false,
            isSupplementaryNotEditable: false,
            phaseEditable: false,
            budgetProposal: undefined,
        };
    }
    const { inscription } = data;
    const budgetProposal = getBudgetProposalFromInscription(
        inscription,
        budgetProposals,
    );
    const inscriptionId = inscriptionIdColumn
        ? (inscription[inscriptionIdColumn] as string)
        : '';

    // Just a flag used to display a specific tooltip : does not have any effect actually
    const isSupplementaryNotEditable =
        budgetProposal.phase === BudgetPhases.SUPPLEMENTARY_BUDGET &&
        isInscriptionVoted(inscription, budgetProposal.phase, statusesSettings);

    const disabledByResource =
        !inscription.canEdit && !inscription.isEditionOpenToResource;

    return {
        inscription,
        inscriptionId,
        disabledByResource,
        isSupplementaryNotEditable,
        canEdit: inscription.canEdit,
        phaseEditable: inscription.isPhaseEditable,
        budgetProposal,
    };
};
